import React from "react";

import PlusSquare from "@uui/PlusSquare";

import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import type { WebhookStatus } from "@app/analytics/types/webhookTypes";

import IconBackground from "@rui/IconBackground";
import Stack, { Row } from "@rui/Stack";

export const FormLabel = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Stack width="100%" spacing={0.5}>
    <Typography variant="bodyPrimaryReg" color="shades.neutral.800">
      {title}
    </Typography>
    <Typography variant="labelReg" color="shades.neutral.600">
      {description}
    </Typography>
  </Stack>
);

export const IconHeading = ({
  Icon,
  children,
}: {
  Icon: typeof PlusSquare;
  children: React.ReactNode;
}) => (
  <Row variant="left" spacing={1}>
    <IconBackground
      height={24}
      borderRadius="4px"
      sx={{ bgcolor: "shades.ghost.500", color: "shades.primary.500" }}
    >
      <Icon />
    </IconBackground>
    <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
      {children}
    </Typography>
  </Row>
);

type StatusLabelProps = TypographyProps & { status: WebhookStatus };

export const StatusLabel = styled<React.FC<StatusLabelProps>>(
  ({ status: _s, ...props }) => <Typography {...props} />
)(({ theme, status }) => ({
  ...theme.typography.bodySecondaryMed,
  width: "fit-content",
  whiteSpace: "nowrap",
  padding: "2px 4px",
  borderRadius: "2px",
  ...(status === "active" && {
    color: theme.palette.shades.meadow[600],
    backgroundColor: theme.palette.shades.meadow[50],
  }),
  ...(status === "errored" && {
    color: theme.palette.shades.orange[600],
    backgroundColor: theme.palette.shades.orange[50],
  }),
  ...(status === "inactive" && {
    color: theme.palette.shades.neutral[500],
    backgroundColor: theme.palette.shades.neutral[50],
  }),
  ...(status === "pending" && {
    color: theme.palette.shades.secondary[600],
    backgroundColor: theme.palette.shades.secondary[50],
  }),
  ...(status === "stopped" && {
    color: theme.palette.shades.negative[600],
    backgroundColor: theme.palette.shades.negative[50],
  }),
}));
